/**
 * Switch Component
 */

import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import type { Tone } from '@tonnie/design-tokens';
import { cn } from '@tonnie/foundation';

import { TONE_CLASSES } from '../lib/tone-classes';

import type { ControlName } from './control-name';

/**
 * A switch shows no words of its own, so it must be named (see ControlName).
 * `tone`: turned on, it wears that tone's fill (a hero's screen) instead of
 * the platform's primary.
 */
export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> &
  ControlName & { tone?: Tone };

export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, tone, ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted',
        tone && TONE_CLASSES[tone].checked,
        className
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          'pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform',
          'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0'
        )}
      />
    </SwitchPrimitives.Root>
  )
);

Switch.displayName = SwitchPrimitives.Root.displayName;
